/**
 * Completion Guards
 *
 * These guards ensure users have completed necessary steps in their journey.
 * They handle business logic requirements beyond basic authentication.
 *
 * Guards:
 * - checkParentRegistration: Ensures premium parents have completed address registration
 * - checkSitterOnboarding: Ensures sitters have completed their onboarding process
 */
import { Route, NavigationGuardNext } from 'vue-router';
import redirectParentRegistration from '@/helpers/redirectParentRegistration';
import { setupApollo } from '@/lib/apollo_setup';
import { redirectIfNotOnboarded, navigateToRoute } from '@/lib/sitterOnboarding/sitterOnboardingState';
import store from '../../store';
import router from '../index';

const checkParentRegistration = async (_to: Route, _from: Route, next: NavigationGuardNext) => {
  const account = store.getters['account/getAccount'];

  if (account?.isPremium()) {
    try {
      const apolloProvider = setupApollo();
      await redirectParentRegistration(apolloProvider);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in redirectParentRegistration:', error);
    }
  }

  next();
};

export const checkSitterOnboarding = async (_to: Route, _from: Route, next: NavigationGuardNext) => {
  const account = store.getters['account/getAccount'];

  if (account?.isSitter()) {
    try {
      const apolloProvider = setupApollo();
      const targetRoute = await redirectIfNotOnboarded(apolloProvider, account, router, _to.path, _to.query);
      if (targetRoute) {
        navigateToRoute(targetRoute, router);
        next(false);
        return;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in checkSitterOnboarding:', error);
      navigateToRoute('/sitter/signup/address', router);
      next(false);
      return;
    }
  }

  next();
};

export default checkParentRegistration;
